import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiPlus, FiUsers, FiToggleLeft, FiToggleRight, FiArrowLeft, FiTrash2, FiLogOut } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { superadminAPI } from '../services/api';

// ── Styled components ──────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
`;

const Header = styled.header`
  background: #1a1a2e;
  color: #fff;
  padding: 0 32px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  h1 {
    font-size: 18px;
    font-weight: 600;
  }

  span {
    background: #4a3f8f;
    color: #c9c3f8;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 4px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #aaa;
`;

const LogoutBtn = styled.button`
  background: transparent;
  color: #aaa;
  border: 1px solid #444;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: #fff;
    border-color: #aaa;
  }
`;

const Content = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h2 {
    font-size: 20px;
    font-weight: 600;
    color: #1a1a2e;
  }
`;

const PrimaryBtn = styled.button`
  background: #4a3f8f;
  color: #fff;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover {
    background: #3a3070;
  }
`;

const BackBtn = styled.button`
  background: transparent;
  color: #4a3f8f;
  border: none;
  padding: 0;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;

  &:hover {
    text-decoration: underline;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const TenantCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  padding: 20px;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;
  opacity: ${p => p.$inactive ? 0.6 : 1};

  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.08);
    border-color: #c9c3f8;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${p => p.$active ? '#e6f7f1' : '#f5f5f5'};
  color: ${p => p.$active ? '#0a7c55' : '#888'};
`;

const CardMeta = styled.div`
  font-size: 13px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin: 12px 0;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const SmallBtn = styled.button`
  flex: 1;
  background: ${p => p.$danger ? '#fff0f0' : '#f5f5f5'};
  color: ${p => p.$danger ? '#c0392b' : '#555'};
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: background 0.15s;

  &:hover {
    background: ${p => p.$danger ? '#ffd5d5' : '#e8e8e8'};
  }
`;

const DetailPanel = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  padding: 24px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    text-align: left;
    padding: 10px 12px;
    color: #666;
    font-weight: 500;
    border-bottom: 1px solid #eee;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  td {
    padding: 12px 12px;
    border-bottom: 1px solid #f5f5f5;
    color: #333;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const RoleBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${p => p.$role === 'admin' ? '#e8f0fe' : '#f0f4ff'};
  color: ${p => p.$role === 'admin' ? '#1a56db' : '#555'};
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.15);

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 20px;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 13px;
    font-weight: 500;
    color: #444;
  }

  input, select {
    padding: 9px 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;

    &:focus {
      border-color: #4a3f8f;
      box-shadow: 0 0 0 3px rgba(74,63,143,0.1);
      outline: none;
    }
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const CancelBtn = styled.button`
  background: #f5f5f5;
  color: #555;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #eee; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: #aaa;
  font-size: 15px;
`;

const ErrorMsg = styled.div`
  background: #fff0f0;
  border: 1px solid #ffb3b3;
  color: #c0392b;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
`;

// ── Component ──────────────────────────────────────────────────────────────────

export default function SuperAdminPage() {
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [formError, setFormError] = useState('');

  const [tenantName, setTenantName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [newRole, setNewRole] = useState('admin');

  const loadTenants = useCallback(async () => {
    try {
      const data = await superadminAPI.listTenants();
      setTenants(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTenantUsers = useCallback(async (tenantId) => {
    const data = await superadminAPI.listTenantUsers(tenantId);
    setTenantUsers(data);
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const handleSelectTenant = async (tenant) => {
    setSelectedTenant(tenant);
    await loadTenantUsers(tenant.id);
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      await superadminAPI.createTenant(tenantName);
      setTenantName('');
      setShowCreateTenant(false);
      loadTenants();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Error creating tenant');
    }
  };

  const handleToggleTenant = async (e, tenantId) => {
    e.stopPropagation();
    await superadminAPI.toggleTenant(tenantId);
    loadTenants();
    if (selectedTenant?.id === tenantId) {
      setSelectedTenant(t => ({ ...t, is_active: !t.is_active }));
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      await superadminAPI.createTenantUser(selectedTenant.id, {
        email: newEmail,
        password: newPassword,
        full_name: newFullName,
        role: newRole,
      });
      setNewEmail(''); setNewPassword(''); setNewFullName(''); setNewRole('admin');
      setShowCreateUser(false);
      loadTenantUsers(selectedTenant.id);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Error creating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    await superadminAPI.deleteTenantUser(selectedTenant.id, userId);
    loadTenantUsers(selectedTenant.id);
  };

  return (
    <Page>
      <Header>
        <HeaderLeft>
          <h1>Quanzico</h1>
          <span>Super Admin</span>
        </HeaderLeft>
        <HeaderRight>
          <span>{user?.email}</span>
          <LogoutBtn onClick={logout}>
            <FiLogOut size={14} />
            Logout
          </LogoutBtn>
        </HeaderRight>
      </Header>

      <Content>
        {selectedTenant ? (
          <>
            <BackBtn onClick={() => setSelectedTenant(null)}>
              <FiArrowLeft size={14} />
              All customers
            </BackBtn>

            <SectionTitle>
              <h2>{selectedTenant.name}</h2>
              <PrimaryBtn onClick={() => { setFormError(''); setShowCreateUser(true); }}>
                <FiPlus size={15} />
                Add user
              </PrimaryBtn>
            </SectionTitle>

            <DetailPanel>
              {tenantUsers.length === 0 ? (
                <EmptyState>No users for this customer.</EmptyState>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantUsers.map(u => (
                      <tr key={u.id}>
                        <td>{u.full_name || '—'}</td>
                        <td>{u.email}</td>
                        <td><RoleBadge $role={u.role}>{u.role}</RoleBadge></td>
                        <td>
                          <Badge $active={u.is_active}>{u.is_active ? 'Active' : 'Inactive'}</Badge>
                        </td>
                        <td>
                          <SmallBtn $danger onClick={() => handleDeleteUser(u.id)}>
                            <FiTrash2 size={13} />
                          </SmallBtn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </DetailPanel>
          </>
        ) : (
          <>
            <SectionTitle>
              <h2>Customers</h2>
              <PrimaryBtn onClick={() => { setFormError(''); setShowCreateTenant(true); }}>
                <FiPlus size={15} />
                New customer
              </PrimaryBtn>
            </SectionTitle>

            {loading ? (
              <EmptyState>Loading...</EmptyState>
            ) : tenants.length === 0 ? (
              <EmptyState>No customers yet. Create the first one!</EmptyState>
            ) : (
              <Grid>
                {tenants.map(tenant => (
                  <TenantCard
                    key={tenant.id}
                    $inactive={!tenant.is_active}
                    onClick={() => handleSelectTenant(tenant)}
                  >
                    <CardHeader>
                      <CardTitle>{tenant.name}</CardTitle>
                      <Badge $active={tenant.is_active}>
                        {tenant.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </CardHeader>
                    <CardMeta>
                      <FiUsers size={13} />
                      Created on {new Date(tenant.date_created).toLocaleDateString('en-US')}
                    </CardMeta>
                    <Divider />
                    <CardActions>
                      <SmallBtn onClick={(e) => handleToggleTenant(e, tenant.id)}>
                        {tenant.is_active
                          ? <><FiToggleRight size={14} /> Deactivate</>
                          : <><FiToggleLeft size={14} /> Activate</>
                        }
                      </SmallBtn>
                      <SmallBtn onClick={(e) => { e.stopPropagation(); handleSelectTenant(tenant); }}>
                        <FiUsers size={14} /> Users
                      </SmallBtn>
                    </CardActions>
                  </TenantCard>
                ))}
              </Grid>
            )}
          </>
        )}
      </Content>

      {showCreateTenant && (
        <Modal onClick={() => setShowCreateTenant(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <h3>New customer</h3>
            <ModalForm onSubmit={handleCreateTenant}>
              {formError && <ErrorMsg>{formError}</ErrorMsg>}
              <FormField>
                <label>Company name</label>
                <input
                  type="text"
                  value={tenantName}
                  onChange={e => setTenantName(e.target.value)}
                  placeholder="e.g. Acme Inc."
                  required
                  autoFocus
                />
              </FormField>
              <ModalActions>
                <CancelBtn type="button" onClick={() => setShowCreateTenant(false)}>Cancel</CancelBtn>
                <PrimaryBtn type="submit">Create customer</PrimaryBtn>
              </ModalActions>
            </ModalForm>
          </ModalCard>
        </Modal>
      )}

      {showCreateUser && (
        <Modal onClick={() => setShowCreateUser(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <h3>New user — {selectedTenant?.name}</h3>
            <ModalForm onSubmit={handleCreateUser}>
              {formError && <ErrorMsg>{formError}</ErrorMsg>}
              <FormField>
                <label>Full name (optional)</label>
                <input
                  type="text"
                  value={newFullName}
                  onChange={e => setNewFullName(e.target.value)}
                  placeholder="John Doe"
                />
              </FormField>
              <FormField>
                <label>Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="user@company.com"
                  required
                />
              </FormField>
              <FormField>
                <label>Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </FormField>
              <FormField>
                <label>Role</label>
                <select value={newRole} onChange={e => setNewRole(e.target.value)}>
                  <option value="admin">Admin (manages the customer's users)</option>
                  <option value="user">User</option>
                </select>
              </FormField>
              <ModalActions>
                <CancelBtn type="button" onClick={() => setShowCreateUser(false)}>Cancel</CancelBtn>
                <PrimaryBtn type="submit">Create user</PrimaryBtn>
              </ModalActions>
            </ModalForm>
          </ModalCard>
        </Modal>
      )}
    </Page>
  );
}
